import { SERVICE_DETAILS_2025 } from "@/constants/servicePackageDetails/service2025";
import { customEvent, SERVICE_DETAILS_2026 } from "goi_common";
import { useRouter } from "next/router";
import { useMemo } from "react";

const SERVICE_ROUTER_PATH = "/service";

const SERVICE_TABLE_BY_YEAR = {
  2025: SERVICE_DETAILS_2025,
  2026: SERVICE_DETAILS_2026,
} as const;

type ServiceYear = keyof typeof SERVICE_TABLE_BY_YEAR;

const DEFAULT_SERVICE_TYPE = "가족장";

const isPremiumPath = (pathname: string) => pathname.includes("premium");

const getServiceTypeWithPremium = (type: string, isPremium: boolean): string => {
  if (!isPremium) return type;
  return type === "가족장" ? "가족장 프리미엄" : "일반장 프리미엄";
};

export const useServicePackage = (year: ServiceYear, initialType?: string) => {
  const router = useRouter();

  const servicePackageDetails = SERVICE_TABLE_BY_YEAR[year];

  const servicePremiumPackageDetails = useMemo(() => {
    const filtered = Object.fromEntries(
      Object.entries(servicePackageDetails).filter(
        ([key]) => key !== "가족장 프리미엄" && key !== "일반장 프리미엄",
      ),
    ) as typeof servicePackageDetails;

    return filtered;
  }, [servicePackageDetails]);

  const currentServiceType = useMemo(() => {
    const queryType = router.isReady ? (router.query.type as string | undefined) : initialType;
    const baseType = queryType || DEFAULT_SERVICE_TYPE;
    const isPremium = isPremiumPath(router.pathname);

    const result = getServiceTypeWithPremium(baseType, isPremium);

    return result;
  }, [router.isReady, router.query.type, router.pathname, initialType]);

  const handleServiceTabChange = (selectedServiceType: string) => {
    router.replace(`${SERVICE_ROUTER_PATH}/${selectedServiceType}`);

    customEvent({
      newGtm: {
        path: "service",
        trigger_and_target: "tab_type-view_type",
        text: selectedServiceType,
      },
    });
  };

  const serviceDetails = useMemo(() => {
    if (!currentServiceType) {
      return undefined;
    }

    const details = servicePackageDetails[currentServiceType as keyof typeof servicePackageDetails];

    return details;
  }, [currentServiceType, servicePackageDetails]);

  return {
    currentServiceType,
    servicePackageDetails,
    servicePremiumPackageDetails,
    handleServiceTabChange,
    serviceDetails,
  };
};
